import { importedWorkouts } from './import.ts';
import type { MainLift } from './lifts.ts';
import { convert, type Mass, type Unit } from './mass.ts';
import { collectWorkouts, finishedWorkouts, fold, unitTimeline, workoutLogs } from './programme.ts';
import type { RepwiseEvent } from './events.ts';
import { e1rm } from './plans/engine.ts';
import type { RpeTable } from './plans/types.ts';
import { classify } from './progression.ts';

/** Progress tab (P01, C17): series, records and consistency, from finished and history-only workouts alike. */

export type PointSource = 'repwise' | 'imported' | 'manual';

export interface LiftPoint {
  at: number;
  weight: Mass;
  outcome: 'success' | 'miss' | 'skipped';
  source: PointSource;
  /** A new best successful weight for this lift. */
  pr: boolean;
  /** The weight went down from the previous session: after a gap of 14+ days it's a break, otherwise a deload. */
  drop?: 'deload' | 'break';
}

const BREAK_MS = 14 * 86_400_000;

interface Session {
  at: number;
  source: PointSource;
  lifts: { lift: MainLift; weight: Mass; reps: (number | null)[]; outcome: LiftPoint['outcome'] }[];
}

const MAIN: readonly MainLift[] = ['squat', 'bench', 'row', 'ohp', 'deadlift'];

/** Every session's weights in one unit (today's, unless asked), so a kg ↔ lb switch doesn't break the chart (ADR-0018). */
function sessions(events: Parameters<typeof finishedWorkouts>[0], unit?: Unit): Session[] {
  const to = unit ?? unitTimeline(events)(events.length - 1);
  const c = (m: Mass, from: Unit) => (from === to ? m : convert(m, from, to, 25));
  const own: Session[] = finishedWorkouts(events).map((w) => ({ at: w.at, source: 'repwise', lifts: w.lifts.map((l) => ({ ...l, weight: c(l.weight, l.unit) })) }));
  const imported: Session[] = importedWorkouts(events).map((w) => ({
    at: w.performedAt,
    source: w.source === 'manual' ? 'manual' : 'imported',
    lifts: w.exercises.flatMap((x) => (x.exercise && (MAIN as string[]).includes(x.exercise) && x.weight !== null && x.reps.length ? [{ lift: x.exercise as MainLift, weight: c(x.weight, w.unit), reps: x.reps, outcome: classify(x.reps, x.reps.length, 5) }] : [])),
  }));
  return [...own, ...imported].sort((a, b) => a.at - b.at);
}

export function liftSeries(events: Parameters<typeof finishedWorkouts>[0], lift: MainLift, unit?: Unit): LiftPoint[] {
  const out: LiftPoint[] = [];
  let best = -1;
  for (const s of sessions(events, unit)) {
    const l = s.lifts.find((x) => x.lift === lift);
    if (!l || l.outcome === 'skipped') continue;
    const prev = out[out.length - 1];
    const pr = l.outcome === 'success' && l.weight > best;
    if (pr) best = l.weight;
    const point: LiftPoint = { at: s.at, weight: l.weight, outcome: l.outcome, source: s.source, pr };
    if (prev && l.weight < prev.weight) point.drop = s.at - prev.at >= BREAK_MS ? 'break' : 'deload';
    out.push(point);
  }
  return out;
}

export interface LiftRecord {
  lift: MainLift;
  /** Heaviest weight with at least one set of 5 or more reps. */
  weight: Mass;
  at: number;
}

export function records(events: Parameters<typeof finishedWorkouts>[0], unit?: Unit): Partial<Record<MainLift, LiftRecord>> {
  const out: Partial<Record<MainLift, LiftRecord>> = {};
  for (const s of sessions(events, unit))
    for (const l of s.lifts) {
      if (!l.reps.some((r) => r !== null && r >= 5)) continue;
      const cur = out[l.lift];
      if (!cur || l.weight > cur.weight) out[l.lift] = { lift: l.lift, weight: l.weight, at: s.at };
    }
  return out;
}

/** Monday 00:00 of the week containing `ms`, in `timeZone`, as a yyyy-mm-dd key. */
function weekKey(ms: number, timeZone: string): string {
  const p = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short' }).formatToParts(ms);
  const get = (t: string) => p.find((x) => x.type === t)!.value;
  const day = Date.UTC(+get('year'), +get('month') - 1, +get('day'));
  const dow = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].indexOf(get('weekday'));
  return new Date(day - dow * 86_400_000).toISOString().slice(0, 10);
}

export interface Consistency {
  /** Oldest first; the last entry is the current week. */
  weeks: { start: string; workouts: number }[];
  onRhythm: number;
  target: number;
}

/** "11 of 13 weeks on rhythm": a week is on rhythm at `target` workouts (3 for 5×5). Break weeks are shown, not hidden. */
export function consistency(events: Parameters<typeof finishedWorkouts>[0], now: number, timeZone = 'UTC', weeks = 13, target = 3): Consistency {
  const counts = new Map<string, number>();
  for (const s of sessions(events)) {
    const k = weekKey(s.at, timeZone);
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  const current = weekKey(now, timeZone);
  const start = Date.parse(`${current}T00:00:00Z`);
  const list = Array.from({ length: weeks }, (_, i) => {
    const k = new Date(start - (weeks - 1 - i) * 7 * 86_400_000).toISOString().slice(0, 10);
    return { start: k, workouts: counts.get(k) ?? 0 };
  });
  return { weeks: list, onRhythm: list.filter((w) => w.workouts >= target).length, target };
}

export interface TimelineRow {
  at: number;
  /** What happened, for the row's wording. */
  what: 'start' | 'session' | 'deload' | 'deload_declined' | 'break' | 'manual' | 'units';
  /** The weight lifted (sessions) or the weight before the change. */
  weight: Mass;
  /** The next target after this row. */
  next: Mass;
  outcome?: 'success' | 'miss' | 'skipped';
  misses: number;
  unit: Unit;
}

/** P02: every decision about one lift, newest first: sessions, deloads, breaks, manual changes, unit switches. */
export function liftTimeline(events: Parameters<typeof finishedWorkouts>[0], lift: MainLift): TimelineRow[] {
  const results = new Map(finishedWorkouts(events).map((w) => [w.workoutId, w.lifts.find((l) => l.lift === lift)]));
  const rows: TimelineRow[] = [];
  let prev: { weight: Mass; reason: string; misses: number } | null = null;
  fold(events, (e, st) => {
    const lp = st.lifts[lift];
    const unit = st.settings.unit;
    const cur = { weight: lp.state.weight, reason: lp.reason, misses: lp.state.misses };
    const changed = !prev || prev.weight !== cur.weight || prev.reason !== cur.reason || prev.misses !== cur.misses;
    if (e.type === 'programme_started' && st.started && !prev) rows.push({ at: e.at, what: 'start', weight: cur.weight, next: cur.weight, misses: 0, unit });
    else if (e.type === 'workout_finished' && changed) {
      const r = results.get(e.workoutId);
      if (r) rows.push({ at: e.at, what: 'session', weight: r.weight, next: cur.weight, outcome: r.outcome, misses: cur.misses, unit });
    } else if (changed && prev) {
      const what = e.type === 'deload_accepted' ? 'deload' : e.type === 'deload_declined' ? 'deload_declined' : e.type === 'break_restart_accepted' ? 'break' : e.type === 'target_overridden' ? 'manual' : e.type === 'units_changed' ? 'units' : null;
      if (what) rows.push({ at: e.at, what, weight: prev.weight, next: cur.weight, misses: cur.misses, unit });
    }
    if (st.started) prev = cur;
  });
  return rows.reverse();
}

export interface AccessoryPoint {
  at: number;
  /** Curls: the dumbbell weight; push-ups: null. */
  weight: Mass | null;
  reps: (number | null)[];
  total: number;
  unit: Unit;
}

/** Accessory progress (P01 `_accessories`): curl dumbbell steps and push-up total reps per session. */
export function accessorySeries(events: Parameters<typeof finishedWorkouts>[0]): { curl: AccessoryPoint[]; pushup: AccessoryPoint[] } {
  const out = { curl: [] as AccessoryPoint[], pushup: [] as AccessoryPoint[] };
  for (const w of workoutLogs(events)) {
    for (const ex of ['curl', 'pushup'] as const) {
      const sets = w.sets.filter((s) => s.exercise === ex && s.kind === 'accessory');
      if (!sets.length || sets.every((s) => s.reps === null)) continue;
      const reps = sets.map((s) => s.reps);
      out[ex].push({ at: w.endedAt, weight: ex === 'curl' ? (sets[0]!.weight ?? null) : null, reps, total: reps.reduce<number>((n, r) => n + (r ?? 0), 0), unit: w.unit });
    }
  }
  return out;
}

export interface Forecast {
  from: { at: number; weight: Mass };
  to: { at: number; low: Mass; high: Mass };
  successRate: number;
}

/**
 * "Show likely range" (US-15, opt-in): where the lift could be in `days` if training continues at the programme's
 * pace (three sessions a week, so squat every session and the others every other one). High = every session a
 * success; low = the recent success rate (last 8 sessions). Not a promise, and deloads aren't modelled.
 */
export function forecast(points: readonly LiftPoint[], lift: MainLift, increment: Mass, days = 28): Forecast | null {
  const last = points[points.length - 1];
  if (!last || points.length < 3) return null;
  const recent = points.slice(-8);
  const successRate = recent.filter((p) => p.outcome === 'success').length / recent.length;
  const perWeek = lift === 'squat' ? 3 : 1.5;
  const sessions = Math.floor((perWeek * days) / 7);
  return { from: { at: last.at, weight: last.weight }, to: { at: last.at + days * 86_400_000, low: last.weight + Math.floor(sessions * successRate) * increment, high: last.weight + sessions * increment }, successRate };
}

// --- J21: any exercise, three metrics ---

export interface ExercisePoint {
  at: number;
  source: PointSource;
  /** Heaviest working set (null for bodyweight). */
  top: Mass | null;
  topReps: number;
  /** Estimated 1RM from the session's best set via the RPE table at RPE 10 ("estimated"); null without a weight or table. */
  e1rm: Mass | null;
  /** Σ reps × weight over working and extra sets (weighted sets only). */
  volume: number;
  reps: number;
}

/** A key for any exercise: its catalogue id, or "name:<name>" for imported exercises Repwise doesn't track. */
export const exerciseKey = (exercise: string | null, name: string) => exercise ?? `name:${name}`;

function ownSessions(events: readonly RepwiseEvent[]) {
  const finished = new Map<string, number>();
  for (const e of events) if (e.type === 'workout_finished' && !finished.has(e.workoutId)) finished.set(e.workoutId, e.at);
  for (const e of events) if (e.type === 'workout_deleted') finished.delete(e.workoutId);
  const out: { at: number; unit: Unit; sets: { exercise: string; name: string; reps: number; weight: Mass | null; extra: boolean }[] }[] = [];
  for (const [id, w] of collectWorkouts(events)) {
    const at = finished.get(id);
    if (at === undefined) continue;
    const name = (ex: string) => w.started.prescriptions.find((p) => p.exercise === ex)?.name ?? ex;
    const sets = [...w.sets.values()].filter((x) => x.kind !== 'warmup' && x.reps !== null && x.reps > 0).map((x) => ({ exercise: x.exercise, name: name(x.exercise), reps: x.reps!, weight: x.weight, extra: x.kind === 'extra' }));
    out.push({ at, unit: w.started.config.gym.unit, sets });
  }
  return out;
}

export function exerciseSeries(events: readonly RepwiseEvent[], key: string, unit?: Unit, table?: RpeTable): ExercisePoint[] {
  const to = unit ?? unitTimeline(events)(events.length - 1);
  const c = (m: Mass, from: Unit) => (from === to ? m : convert(m, from, to, 25));
  const point = (at: number, source: PointSource, sets: { reps: number; weight: Mass | null; extra: boolean }[]): ExercisePoint | null => {
    if (!sets.length) return null;
    const work = sets.filter((x) => !x.extra);
    const top = work.reduce<{ reps: number; weight: Mass | null } | null>((m, x) => (x.weight !== null && (m?.weight == null || x.weight > m.weight || (x.weight === m.weight && x.reps > m.reps)) ? x : m), null);
    let best: number | null = null;
    if (table) for (const x of work) if (x.weight !== null && table.reps.includes(x.reps)) best = Math.max(best ?? 0, e1rm(table, x.weight, x.reps, 10));
    return { at, source, top: top?.weight ?? null, topReps: top?.reps ?? Math.max(...work.map((x) => x.reps), 0), e1rm: best === null ? null : Math.round(best), volume: sets.reduce((n, x) => n + (x.weight ?? 0) * x.reps, 0), reps: sets.reduce((n, x) => n + x.reps, 0) };
  };
  const out: ExercisePoint[] = [];
  for (const w of ownSessions(events)) {
    const p = point(w.at, 'repwise', w.sets.filter((x) => x.exercise === key).map((x) => ({ ...x, weight: x.weight === null ? null : c(x.weight, w.unit) })));
    if (p) out.push(p);
  }
  for (const w of importedWorkouts(events)) {
    for (const x of w.exercises) {
      if (exerciseKey(x.exercise, x.name) !== key) continue;
      const sets = x.reps.filter((r): r is number => r !== null && r > 0).map((r) => ({ reps: r, weight: x.weight === null ? null : c(x.weight, w.unit), extra: false }));
      const p = point(w.performedAt, w.source === 'manual' ? 'manual' : 'imported', sets);
      if (p) out.push(p);
    }
  }
  return out.sort((a, b) => a.at - b.at);
}

/** Every exercise with history, bookmarked first, then the most recent. */
export function exerciseIndex(events: readonly RepwiseEvent[]): { key: string; name: string; sessions: number; lastAt: number; bookmarked: boolean }[] {
  const m = new Map<string, { key: string; name: string; sessions: number; lastAt: number }>();
  const add = (key: string, name: string, at: number) => {
    const cur = m.get(key) ?? { key, name, sessions: 0, lastAt: 0 };
    cur.sessions++;
    cur.lastAt = Math.max(cur.lastAt, at);
    m.set(key, cur);
  };
  for (const w of ownSessions(events)) for (const k of new Set(w.sets.map((x) => x.exercise))) add(k, w.sets.find((x) => x.exercise === k)!.name, w.at);
  for (const w of importedWorkouts(events)) for (const x of w.exercises) if (x.reps.some((r) => r !== null && r > 0)) add(exerciseKey(x.exercise, x.name), x.name, w.performedAt);
  const marks = bookmarks(events);
  return [...m.values()].map((x) => ({ ...x, bookmarked: marks.has(x.key) })).sort((a, b) => Number(b.bookmarked) - Number(a.bookmarked) || b.lastAt - a.lastAt);
}

export function bookmarks(events: readonly RepwiseEvent[]): Set<string> {
  const on = new Set<string>();
  for (const e of events) if (e.type === 'exercise_bookmarked') e.on ? on.add(e.exercise) : on.delete(e.exercise);
  return on;
}

// --- J26: added weight on bodyweight exercises ---

/** Whether the user opted in to progressing added weight on this exercise. */
export function addedWeightProgress(events: readonly RepwiseEvent[], exercise: string): boolean {
  let on = false;
  for (const e of events) if (e.type === 'added_weight_progress' && e.exercise === exercise) on = e.on;
  return on;
}

/**
 * The added weight to suggest for a bodyweight exercise's next workout, or null for bodyweight only. Off (the
 * default): null. On: the last finished workout's added weight, plus `increment` when every set hit its target.
 */
export function suggestedAddedWeight(events: readonly RepwiseEvent[], exercise: string, increment: Mass): Mass | null {
  if (!addedWeightProgress(events, exercise)) return null;
  const finished = new Map<string, number>();
  for (const e of events) if (e.type === 'workout_finished' && !finished.has(e.workoutId)) finished.set(e.workoutId, e.at);
  for (const e of events) if (e.type === 'workout_deleted') finished.delete(e.workoutId);
  let best: { at: number; weight: Mass; hit: boolean } | null = null;
  for (const [id, w] of collectWorkouts(events)) {
    const at = finished.get(id);
    if (at === undefined || (best && at < best.at)) continue;
    const p = w.started.prescriptions.find((x) => x.exercise === exercise);
    const sets = [...w.sets.values()].filter((x) => x.exercise === exercise && x.kind === 'work' && x.reps !== null);
    if (!p || !sets.length) continue;
    const weight = Math.max(0, ...sets.map((x) => x.weight ?? 0));
    if (weight <= 0) continue;
    const target = (i: number) => p.setPlan?.[i]?.reps ?? p.reps;
    const n = p.setPlan?.length ?? p.sets;
    const hit = sets.length >= n && sets.every((x) => x.reps! >= target(x.index) && (x.weight ?? 0) >= weight);
    best = { at, weight, hit };
  }
  return best ? best.weight + (best.hit ? increment : 0) : null;
}
